import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { dataflowApi } from "@/services/api/dataflow";
import { useDataFlowStore } from "@/stores/dataflowStore";
import type { Instance, Template } from "@/types/dataflow";

function statusCount(instances: Instance[], status: Instance["status"]): number {
  return instances.filter((item) => item.status === status).length;
}

function StatusBadge({ status }: { status: Instance["status"] }) {
  const colors: Record<string, string> = {
    QUEUED: "bg-slate-500/15 text-slate-400 border-slate-500/30",
    RUNNING: "bg-sky-500/15 text-sky-400 border-sky-500/30",
    COMPLETED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    FAILED: "bg-rose-500/15 text-rose-400 border-rose-500/30",
    CANCELLED: "bg-orange-500/15 text-orange-400 border-orange-500/30",
    PENDING: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  };

  return (
    <span
      className={`
        inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium
        rounded border ${colors[status] || colors.PENDING}
      `}
    >
      {status}
    </span>
  );
}

function fmtTime(raw?: string): string {
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatTemplateTriggers(template: Template): string {
  const modes: string[] = [];
  if (template.schedule_kind) {
    const time = template.schedule_config_json?.time;
    modes.push(time ? `${template.schedule_kind} @ ${time}` : String(template.schedule_kind));
  } else {
    modes.push("manual");
  }
  if ((template.depends_on_template_ids || []).length > 0) {
    modes.push("upstream");
  }
  return modes.join(" + ");
}

function formatUpstreamTemplates(
  template: Template,
  templateById: Map<number, Template>,
): string {
  const depIds = template.depends_on_template_ids || [];
  if (depIds.length === 0) return "none";
  return depIds
    .map((depId) => {
      const upstream = templateById.get(depId);
      return upstream ? `${upstream.name} (#${depId})` : `#${depId}`;
    })
    .join(", ");
}

export function TemplateRuntimeView() {
  const queryClient = useQueryClient();
  const { setSelectedInstance, setSelectedTemplate } = useDataFlowStore();

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["dataflow", "templates"],
    queryFn: () => dataflowApi.getTemplates(),
    refetchInterval: 5000,
  });

  const { data: instances = [], isLoading: instancesLoading } = useQuery({
    queryKey: ["dataflow", "instances"],
    queryFn: () => dataflowApi.getInstances(),
    refetchInterval: 3000,
  });

  const runMutation = useMutation({
    mutationFn: (templateId: number) => dataflowApi.runTemplate(String(templateId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dataflow", "instances"] });
      queryClient.invalidateQueries({ queryKey: ["dataflow", "batches"] });
    },
  });

  if (templatesLoading || instancesLoading) {
    return <div className="p-4 text-sm text-[var(--muted)]">Loading template runtime view...</div>;
  }

  const grouped = templates.map((template: Template) => {
    const related = instances
      .filter((instance) => instance.template_id === template.id)
      .slice()
      .sort((left, right) => right.id - left.id);
    return { template, instances: related };
  });
  const templateById = new Map(templates.map((template: Template) => [template.id, template]));

  return (
    <div className="h-full overflow-auto p-3 pb-20">
      <div className="space-y-3">
        {grouped.map(({ template, instances: related }) => {
          const latest = related[0];
          const upstreamText = formatUpstreamTemplates(template, templateById);
          return (
            <div key={template.id} className="rounded border border-[var(--border)] bg-[var(--panel)]">
              <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
                <button
                  className="text-left"
                  onClick={() => setSelectedTemplate(String(template.id))}
                  type="button"
                >
                  <div className="text-sm font-semibold text-slate-100">{template.name}</div>
                  <div className="text-[11px] text-[var(--muted)]">{template.task_type} · template #{template.id}</div>
                  <div className="text-[11px] text-[var(--muted)]">Trigger: {formatTemplateTriggers(template)}</div>
                  <div className="text-[11px] text-[var(--muted)] truncate" title={upstreamText}>
                    Upstream: {upstreamText}
                  </div>
                </button>
                <div className="flex items-center gap-3">
                  <div className="text-[11px] text-[var(--muted)]">
                    <span className="text-emerald-300">{statusCount(related, "COMPLETED")}</span> ok /
                    <span className="text-sky-300"> {statusCount(related, "RUNNING")}</span> run /
                    <span className="text-rose-300"> {statusCount(related, "FAILED")}</span> fail
                  </div>
                  <button
                    onClick={() => runMutation.mutate(template.id)}
                    disabled={runMutation.isPending}
                    className="rounded bg-[#16a34a] px-2 py-1 text-xs text-white hover:bg-[#15803d] disabled:opacity-50"
                    type="button"
                  >
                    Run
                  </button>
                </div>
              </div>
              {/* Instances list with indentation */}
              <div className="border-t border-[var(--border)]">
                <div className="pl-4 border-l-2 border-[var(--border)]/50 ml-4 my-1">
                  {related.slice(0, 8).map((instance, index) => (
                    <button
                      key={instance.id}
                      className={`
                        flex w-full items-center justify-between px-3 py-2 text-left
                        hover:bg-white/[0.03] rounded-md my-0.5
                        ${index > 0 ? 'border-t border-[var(--border)]/30' : ''}
                      `}
                      onClick={() => setSelectedInstance(instance.id)}
                      type="button"
                    >
                      <div className="flex items-center gap-2">
                        {/* Tree connector */}
                        <span className="font-mono text-[var(--muted)]/50 select-none text-xs">
                          {index === related.slice(0, 8).length - 1 && related.length <= 8 ? '└─' : '├─'}
                        </span>
                        <div className="text-xs text-slate-200">
                          #{instance.id} · {instance.name || instance.task_type}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-[11px]">
                        <span className="text-[var(--muted)]">{fmtTime(instance.finished_at || instance.started_at || instance.created_at)}</span>
                        <StatusBadge status={instance.status} />
                      </div>
                    </button>
                  ))}
                  {related.length === 0 && (
                    <div className="px-3 py-2 text-[11px] text-[var(--muted)] flex items-center gap-2">
                      <span className="font-mono text-[var(--muted)]/30">└─</span>
                      No runtime instances yet.
                    </div>
                  )}
                  {related.length > 8 && (
                    <div className="px-3 py-2 text-[11px] text-[var(--muted)] flex items-center gap-2">
                      <span className="font-mono text-[var(--muted)]/30">└─</span>
                      Showing latest 8 of {related.length}.
                    </div>
                  )}
                </div>
              </div>
              {latest && (
                <div className="border-t border-[var(--border)] px-3 py-1.5 text-[11px] text-[var(--muted)]">
                  Latest: #{latest.id} {latest.status} @ {fmtTime(latest.finished_at || latest.started_at || latest.created_at)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
